import React, { useState } from 'react';
import { useT } from './i18n/I18nContext';

export default function QCFailModal({ unit, reasons, onSubmit, onCancel }) {
    const t = useT();
    const [reason, setReason] = useState('');
    // Saved-note text, keyed implicitly by current `reason` — switching reason wipes it.
    const [savedNote, setSavedNote] = useState('');
    // Whether the textarea is currently expanded for the selected chip.
    const [noteEditing, setNoteEditing] = useState(false);
    // Draft text in the textarea (separate from savedNote so Cancel discards).
    const [draft, setDraft] = useState('');

    const label = unit.playerName
        ? `${unit.playerName}${unit.playerNumber ? ` #${unit.playerNumber}` : ''}`
        : unit.title;

    function handleSelectReason(r) {
        if (r === reason) return;
        setReason(r);
        // Switching the selected reason wipes any in-progress / saved note —
        // notes are conceptually tied to "the failure I'm reporting now".
        setSavedNote('');
        setDraft('');
        setNoteEditing(false);
    }

    function openNoteEditor(e) {
        e.preventDefault();      // don't toggle the underlying <label>'s radio
        e.stopPropagation();
        setDraft(savedNote);
        setNoteEditing(true);
    }

    function saveNote() {
        const trimmed = draft.trim().slice(0, 500);
        setSavedNote(trimmed);
        setNoteEditing(false);
    }

    function cancelNote() {
        setNoteEditing(false);
        setDraft('');
    }

    function notePreview(text) {
        if (!text) return '';
        return text.length > 80 ? text.slice(0, 80) + '…' : text;
    }

    function handleSubmit() {
        // If the operator typed something but didn't tap Save, take the draft
        // value at submit time. Otherwise take the saved value. Empty → null.
        const effective = (noteEditing ? draft : savedNote).trim().slice(0, 500);
        onSubmit(reason, 'rework', effective || null);
    }

    return (
        <div className={`qcp-modal-overlay${noteEditing ? ' qcp-modal-overlay--note-editing' : ''}`}>
            <div className={`qcp-modal${noteEditing ? ' qcp-modal--note-editing' : ''}`}>
                <div className="qcp-modal-title">{t('QC Failed —')} {label}</div>
                {!noteEditing && (
                    <div className="qcp-modal-sub">{t('Select the failure reason')}</div>
                )}
                <div className="qcp-modal-reasons">
                    {reasons.map(r => {
                        const selected = reason === r;
                        const showEditor = selected && noteEditing;
                        const showPreview = selected && !noteEditing && savedNote;
                        // While a note is being typed, hide the non-selected
                        // reason chips so the modal stays short — this keeps
                        // the textarea in the top half of the screen on iPad
                        // landscape (the keyboard covers the bottom ~50%).
                        if (noteEditing && !selected) return null;
                        return (
                            <div
                                key={r}
                                className={`qcp-modal-reason${selected ? ' qcp-modal-reason--selected' : ''}`}
                            >
                                <label className="qcp-modal-reason__row">
                                    <input
                                        type="radio" name="reason" value={r}
                                        checked={selected}
                                        onChange={() => handleSelectReason(r)}
                                    />
                                    <span className="qcp-modal-reason__label">{t(r)}</span>
                                    {selected && !noteEditing && (
                                        <button
                                            type="button"
                                            className="qcp-modal-reason__note-pill"
                                            onClick={openNoteEditor}
                                        >
                                            {savedNote ? t('Edit Note') : t('Add Note')}
                                        </button>
                                    )}
                                </label>
                                {showPreview && (
                                    <div className="qcp-modal-reason__note-preview">
                                        {notePreview(savedNote)}
                                    </div>
                                )}
                                {showEditor && (
                                    <div className="qcp-modal-reason__note-edit">
                                        <textarea
                                            className="qcp-modal-reason__note-textarea"
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            placeholder={t('Type a note for this QC failure (optional)')}
                                            rows={3}
                                            maxLength={500}
                                            autoFocus
                                            inputMode="text"
                                            // 2026-08-03: on iPad landscape the on-screen
                                            // keyboard covers the bottom half of the screen.
                                            // Scroll the textarea to the TOP of the visible
                                            // viewport on focus so it stays out from behind
                                            // the keyboard. Wrapped in a small timeout so
                                            // the keyboard slide-up finishes first.
                                            onFocus={(e) => {
                                                const el = e.currentTarget;
                                                setTimeout(() => {
                                                    try { el.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch {}
                                                }, 300);
                                            }}
                                        />
                                        <div className="qcp-modal-reason__note-actions">
                                            <button
                                                type="button"
                                                className="qcp-modal-btn qcp-modal-btn--secondary"
                                                onClick={cancelNote}
                                            >
                                                {t('Cancel')}
                                            </button>
                                            <button
                                                type="button"
                                                className="qcp-modal-btn qcp-modal-btn--primary"
                                                onClick={saveNote}
                                            >
                                                {t('Save')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {!noteEditing && (
                    <div className="qcp-modal-actions">
                        <button className="qcp-modal-btn qcp-modal-btn--secondary" onClick={onCancel}>
                            {t('Cancel')}
                        </button>
                        <button
                            className="qcp-modal-btn qcp-modal-btn--danger"
                            onClick={handleSubmit}
                            disabled={!reason}
                        >
                            {t('Send for Rework')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
